import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import "./TaskDetailsModal.css";
import { Avatar, CircularProgress, Tooltip, IconButton, AvatarGroup } from "@mui/material";
import {
  FaChevronDown,
  FaChevronUp,
  FaArrowRight,
  FaHistory,
  FaTimes,
  FaExpandAlt,
  FaGripVertical,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import TaskAttachmentsPanel, { buildAttachmentList } from "./TaskAttachmentsPanel";
import UserAvatarTooltip from "./common/UserAvatarTooltip";

/* ─── constants (module-level, never re-created) ────────────────────────── */

const META_DASH = "—";

const AVATAR_COLORS = ["#3b82f6", "#10b981", "#8b5cf6", "#f59e0b", "#6366f1", "#ec4899"];

const STATUS_CONFIG = {
  completed: { bg: "#dcfce7", text: "#166534", border: "#bbf7d0", dot: "#16a34a" },
  pending: { bg: "#fef9c3", text: "#854d0e", border: "#fef08a", dot: "#ca8a04" },
  delegated: { bg: "#f3e8ff", text: "#6b21a8", border: "#e9d5ff", dot: "#9333ea" },
  wip: { bg: "#ffedd5", text: "#9a3412", border: "#fed7aa", dot: "#ea580c" },
  "in progress": { bg: "#ffedd5", text: "#9a3412", border: "#fed7aa", dot: "#ea580c" },
  "in-progress": { bg: "#ffedd5", text: "#9a3412", border: "#fed7aa", dot: "#ea580c" },
  revalidate: { bg: "#e0f2fe", text: "#0369a1", border: "#bae6fd", dot: "#0ea5e9" },
  reopen: { bg: "#fee2e2", text: "#991b1b", border: "#fecaca", dot: "#ef4444" },
};

const STATUS_FALLBACK = { bg: "#f3f4f6", text: "#374151", border: "#e5e7eb", dot: "#6b7280" };

const PRIORITY_CONFIG = {
  urgent: { color: "#dc2626", icon: "!!!" },
  high: { color: "#ea580c", icon: "!!" },
  medium: { color: "#3b82f6", icon: "!" },
  low: { color: "#6b7280", icon: "↓" },
};

const PRIORITY_FALLBACK = { color: "#6b7280", icon: "–" };



/* ─── pure helpers (stable references, no side-effects) ─────────────────── */

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDateShort(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" });
}

function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
}

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  return `${formatDate(dateStr)} • ${formatTime(dateStr)}`;
}

function getInitials(name, email) {
  if (name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const letters = `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.trim();
    if (letters) return letters.toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "NA";
}

function getAvatarColor(index) {
  return AVATAR_COLORS[index % AVATAR_COLORS.length];
}

function getStatusConfig(status) {
  if (!status) return STATUS_FALLBACK;
  return STATUS_CONFIG[status.toLowerCase()] ?? STATUS_FALLBACK;
}

function getPriorityConfig(p) {
  if (!p) return PRIORITY_FALLBACK;
  return PRIORITY_CONFIG[p.toLowerCase()] ?? PRIORITY_FALLBACK;
}

/**
 * Normalise full name from various API shapes.
 * Returns empty string when nothing is found.
 */
function resolveFullName(first, last) {
  return [first, last].filter(Boolean).join(" ").trim();
}

/* ─── memoised sub-components ───────────────────────────────────────────── */

/** Section heading with left blue bar accent */
const SectionTitle = React.memo(({ title }) => (
  <div className="tdm-section-title">
    <span className="tdm-section-bar" />
    <span className="tdm-section-label">{title}</span>
  </div>
));
SectionTitle.displayName = "SectionTitle";

/* ── SubtaskRow ─────────────────────────────────────────────────────────── */

const SubtaskRow = React.memo(({ st, onNavigate }) => {
  const stStatusCfg = useMemo(() => getStatusConfig(st.status), [st.status]);
  const stAssigneeName = useMemo(
    () =>
      resolveFullName(
        st.assignee?.firstName || st.assignee?.FirstName || st?.user?.first_name,
        st.assignee?.lastName || st.assignee?.LastName || st?.user?.last_name
      ) || "Unassigned",
    [st.assignee, st.user]
  );

  const firstName = useMemo(() => {
    return st.assignee?.firstName || st.assignee?.FirstName || st?.user?.first_name;
  }, [st.assignee])

  const lastName = useMemo(() => {
    return st.assignee?.lastName || st.assignee?.LastName || st?.user?.last_name;
  }, [st.assignee])

  const stTaskId = useMemo(() => {
    const raw = st?.ticket_id || st?.ticketId || st?.ticketNumber || st?.ticket_number || null;
    return raw ? String(raw) : st?.id ? `T-${st.id.slice(0, 4)}` : "T-N/A";
  }, [st]);

  return (
    <div className="tdm-subtask-row" onClick={() => onNavigate(st.id)}>
      <div className="tdm-st-main">
        <FaGripVertical className="tdm-st-grip" />
        <span className="tdm-st-ticket">{stTaskId}</span>
        <span className="tdm-st-title" title={st.title}>{st.title}</span>
      </div>

      <div className="tdm-st-right">
        <UserAvatarTooltip
          firstName={firstName}
          lastName={lastName}
          email={st.assignee?.email || st.user?.email}
          displayName={stAssigneeName}
          avatarSx={{ width: 24, height: 24, fontSize: "0.65rem" }}
        />
        <span
          className="tdm-st-status"
          style={{
            background: stStatusCfg.bg,
            color: stStatusCfg.text,
            border: `1px solid ${stStatusCfg.border}`,
          }}
        >
          {st.status || "Pending"}
        </span>
      </div>
    </div>
  );
});
SubtaskRow.displayName = "SubtaskRow";

/* ── DelegatedTaskItem ──────────────────────────────────────────────────── */

const DelegatedTaskItem = React.memo(({ d }) => {
  const cfg = useMemo(() => getStatusConfig(d.status), [d.status]);
  return (
    <div className="tdm-delegated-item">
      <Avatar sx={{ width: 26, height: 26, fontSize: "0.65rem", bgcolor: "#8b5cf6", flexShrink: 0 }}>
        {getInitials(d.assigneeName, d.assigneeEmail)}
      </Avatar>
      <div className="tdm-delegated-info">
        <span className="tdm-delegated-name">{d.assigneeName}</span>
        <span className="tdm-delegated-sub">{d.title}</span>
        <span className="tdm-delegated-dates">
          📅 {formatDate(d.createdAt)}{d.dueDate ? `  ⏰ ${formatDate(d.dueDate)}` : ""}
        </span>
      </div>
      <span className="tdm-delegated-status" style={{ background: cfg.bg, color: cfg.text }}>
        {d.status}
      </span>
    </div>
  );
});
DelegatedTaskItem.displayName = "DelegatedTaskItem";

/* ── TimelineEvent ──────────────────────────────────────────────────────── */

const TimelineEvent = React.memo(({ event, idx }) => {
  const cfg = useMemo(() => getStatusConfig(event.status), [event.status]);
  return (
    <div key={event.id || idx} className="tdm-timeline-item">
      <span className="tdm-timeline-dot" style={{ background: cfg.dot }} />
      <div className="tdm-timeline-content">
        <div className="tdm-timeline-row">
          <span className="tdm-timeline-status">{event.status}</span>
          <span className="tdm-timeline-by">by {event.createdBy}</span>
        </div>
        {event.remark && <p className="tdm-timeline-remark">{event.remark}</p>}
        <span className="tdm-timeline-date">{formatDateTime(event.createdAt)}</span>
      </div>
    </div>
  );
});
TimelineEvent.displayName = "TimelineEvent";

/* ── ActivityTimelineEvent ──────────────────────────────────────────────── */

const ActivityTimelineEvent = React.memo(({ event, idx }) => {
  const eventUser = event.user || {};
  const userName = useMemo(
    () => resolveFullName(eventUser.first_name, eventUser.last_name) || "System",
    [eventUser.first_name, eventUser.last_name]
  );
  const eventStatus = useMemo(() => getStatusConfig(event.action), [event.action]);

  return (
    <div className="tdm-timeline-item" style={{ paddingBottom: "20px" }}>
      <span
        className="tdm-timeline-dot"
        style={{ background: eventStatus.dot, left: "-29px", width: "8px", height: "8px", top: "6px" }}
      />
      <div className="tdm-timeline-content">
        <div className="tdm-timeline-row">
          <span className="tdm-timeline-status" style={{ color: eventStatus.text }}>{event.action}</span>
          <span className="tdm-timeline-by">by {userName}</span>
        </div>
        {event.remarks && <p className="tdm-timeline-remark">{event.remarks}</p>}
        <span className="tdm-timeline-date">{formatDateTime(event.created_at)}</span>
        {event.attachments?.length > 0 && (
          <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {event.attachments.map((att) => (
              <div
                key={att.id}
                style={{
                  fontSize: "0.7rem",
                  padding: "2px 8px",
                  background: "#f3f4f6",
                  borderRadius: "4px",
                  border: "1px solid #e5e7eb",
                  color: "#374151",
                }}
              >
                📎 {att.original_name}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});
ActivityTimelineEvent.displayName = "ActivityTimelineEvent";

/* ── MemberCard ─────────────────────────────────────────────────────────── */

const MemberCard = React.memo(({ member, idx, isExpanded, onToggle }) => {
  const mStatus = useMemo(() => getStatusConfig(member.status), [member.status]);
  const filteredTimeline = useMemo(
    () =>
      (member.timeline ?? []).filter(
        (e) => !(e.status?.toLowerCase() || "").includes("released")
      ),
    [member.timeline]
  );

  return (
    <div className={`tdm-member-card${isExpanded ? " tdm-member-card--expanded" : ""}`}>
      <button className="tdm-member-row" onClick={onToggle}>
        <Avatar
          sx={{
            width: 34,
            height: 34,
            fontSize: "0.7rem",
            fontWeight: 700,
            bgcolor: getAvatarColor(idx),
            flexShrink: 0,
          }}
        >
          {getInitials(member.name, member.email)}
        </Avatar>
        <div className="tdm-member-info">
          <span className="tdm-member-name">{member.name}</span>
          <span className="tdm-member-email">{member.email || "No email"}</span>
        </div>
        <span className="tdm-member-status" style={{ color: mStatus.text }}>
          {member.status}
        </span>
        <span className="tdm-member-chevron">
          {isExpanded ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
        </span>
      </button>

      {isExpanded && (
        <div className="tdm-member-expanded">
          {/* delegated tasks */}
          {member.delegatedTasks?.length > 0 && (
            <div className="tdm-delegated">
              <p className="tdm-delegated-title">
                <FaArrowRight size={9} style={{ marginRight: 6 }} />
                Delegated Assignments ({member.delegatedTasks.length})
              </p>
              {member.delegatedTasks.map((d) => (
                <DelegatedTaskItem key={d.id} d={d} />
              ))}
            </div>
          )}

          {/* per-member timeline */}
          <div className="tdm-timeline">
            <p className="tdm-timeline-title">
              <FaHistory size={10} style={{ marginRight: 6 }} />
              Activity Timeline
            </p>
            <div className="tdm-timeline-list">
              {filteredTimeline.length > 0 ? (
                filteredTimeline.map((event, eIdx) => (
                  <TimelineEvent key={event.id || eIdx} event={event} idx={eIdx} />
                ))
              ) : (
                <p className="tdm-timeline-empty">No activity recorded</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
MemberCard.displayName = "MemberCard";

/* ─── helper: build member list from raw API shapes ─────────────────────── */

function buildMemberList(details, rawTask) {
  const result = [];
  const delegationChildren = details?.delegationChildren ?? rawTask?.delegationChildren ?? [];



  if (Array.isArray(details?.assignees) && details.assignees.length > 0) {
    details.assignees.forEach((assignee, index) => {
      const name =
        assignee.name ||
        resolveFullName(
          assignee.first_name || assignee.FirstName || assignee.firstName,
          assignee.last_name || assignee.LastName || assignee.lastName
        ) ||
        "Unassigned";

      const delegatedTasks = delegationChildren.map((child, idx) => ({
        id: child?.id || `delegated-${idx}`,
        assigneeName:
          resolveFullName(
            child?.assignee?.first_name || child?.assignee?.FirstName,
            child?.assignee?.last_name || child?.assignee?.LastName
          ) || "Unassigned",
        assigneeEmail: child?.assignee?.email || null,
        status: child?.status || "Pending",
        createdAt: child?.created_at,
        dueDate: child?.due_date,
        title: child?.title || "Delegated Task",
        remark: child?.description || child?.statusRemark || null,
      }));

      result.push({
        id: assignee.taskId || assignee.userId || assignee.id || `assignee-${index}`,
        name,
        email: assignee.email || null,
        status: assignee.status || rawTask?.status || "Pending",
        createdAt: assignee.createdAt || rawTask?.created_at,
        createdBy:
          rawTask?.given_by_name ||
          resolveFullName(
            rawTask?.givenBy?.first_name || rawTask?.givenBy?.FirstName || rawTask?.GivenByFirstName,
            rawTask?.givenBy?.last_name || rawTask?.givenBy?.LastName || rawTask?.GivenByLastName
          ) ||
          "N/A",
        dueDate: assignee.dueDate || rawTask?.due_date,
        delegatedTasks,
        timeline: assignee.timeline || [],
      });
    });
  } else if (rawTask) {
    const firstName =
      rawTask.assignee?.first_name || rawTask.assignee?.FirstName ||
      rawTask.FirstName || rawTask.first_name || rawTask.assignee_name || "";
    const lastName =
      rawTask.assignee?.last_name || rawTask.assignee?.LastName ||
      rawTask.LastName || rawTask.last_name || rawTask.assignee_last || "";

    const memberTimeline = [];
    if (rawTask.created_at) {
      memberTimeline.push({
        id: `assigned-${rawTask.id}`,
        status: "Assigned",
        remark: null,
        createdAt: rawTask.created_at,
        createdBy: rawTask.given_by_name || "System",
      });
    }
    if (Array.isArray(rawTask.actions)) {
      rawTask.actions.forEach((a) =>
        memberTimeline.push({
          id: a.id,
          status: a.status || a.action,
          remark: a.remark || a.remarks || null,
          createdAt: a.createdAt || a.created_at,
          createdBy: a.createdBy || "System",
        })
      );
    }

    result.push({
      id: rawTask.id || "main-assignee",
      name: resolveFullName(firstName, lastName) || "Unassigned",
      email:
        rawTask.assignee?.email || rawTask.email || rawTask.assignee_email || null,
      status: rawTask.status || "Pending",
      createdAt: rawTask.created_at,
      createdBy:
        rawTask.given_by_name ||
        resolveFullName(
          rawTask.givenBy?.first_name || rawTask.givenBy?.FirstName || rawTask?.GivenByFirstName,
          rawTask.givenBy?.last_name || rawTask.givenBy?.LastName || rawTask?.GivenByLastName
        ) ||
        "N/A",
      dueDate: rawTask.due_date,
      delegatedTasks: delegationChildren.map((child, idx) => ({
        id: child?.id || `delegated-${idx}`,
        assigneeName:
          resolveFullName(
            child?.assignee?.first_name || child?.assignee?.FirstName,
            child?.assignee?.last_name || child?.assignee?.LastName
          ) || "Unassigned",
        assigneeEmail: child?.assignee?.email || null,
        status: child?.status || "Pending",
        createdAt: child?.created_at,
        dueDate: child?.due_date,
        title: child?.title || "Delegated Task",
        remark: child?.description || child?.statusRemark || null,
      })),
      timeline: memberTimeline,
    });
  }

  return result;
}

/* ─── Main Component ─────────────────────────────────────────────────────── */

const TaskDetailsModal = React.memo(
  ({ open, onClose, task, showGivenBy = false, variant = "modal" }) => {
    const navigate = useNavigate();
    const currentUser = useSelector((state) => state.auth.user);

    const [details, setDetails] = useState(task || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedMemberId, setSelectedMemberId] = useState(null);
    const [subtasks, setSubtasks] = useState([]);
    const [loadingSubtasks, setLoadingSubtasks] = useState(false);
    const [timeline, setTimeline] = useState([]);
    const [loadingTimeline, setLoadingTimeline] = useState(false);

    /* ── fetch-lock/persistence ───────────────────────────────────────── */
    const cancelledRef = useRef(false);
    const activeTaskRef = useRef({ taskId: null, controller: null });

    /* ── data-fetching effect ─────────────────────────────────────────── */
    useEffect(() => {
      const taskId = task?.id;

      // Case A: Modal is closed or no task ID -> Cleanup and reset
      if ((!open && variant !== "full-page") || !taskId) {
        if (activeTaskRef.current.controller) {
          activeTaskRef.current.controller.abort();
        }
        activeTaskRef.current = { taskId: null, controller: null };

        if (!open && variant !== "drawer") {
          setDetails(null);
          setSelectedMemberId(null);
          setError(null);
          setLoading(false);
          setSubtasks([]);
          setTimeline([]);
        }
        return;
      }

      // Case B: Already fetching/showing this specific task -> Skip redundant fetch
      // This survives Strict Mode's double-mount and parent re-renders.
      if (activeTaskRef.current.taskId === taskId) {
        return;
      }

      // Case C: New task ID -> Abort previous and start new fetch
      if (activeTaskRef.current.controller) {
        activeTaskRef.current.controller.abort();
      }

      const controller = new AbortController();
      activeTaskRef.current = { taskId, controller };

      // Sync initial data (lean props)
      if (!details || details.id !== taskId) {
        setDetails(task);
      }

      setLoading(true);
      setLoadingSubtasks(true);
      setLoadingTimeline(true);

      /* ── primary task details ── */
      const detailsParams = {};
      if (task?.is_grouped === 1 || task?.is_grouped === "1" || task?.is_grouped === true) {
        detailsParams.is_grouped = 1;
      }

      axiosInstance
        .get(`api/task/${taskId}`, { params: detailsParams, signal: controller.signal })
        .then(({ data }) => {
          if (activeTaskRef.current.taskId !== taskId) return;
          const resolved = data?.data && typeof data.data === "object" ? data.data : data;
          setDetails(resolved || null);
        })
        .catch((err) => {
          if (!controller.signal.aborted && activeTaskRef.current.taskId === taskId) {
            setError(err.message || "Failed to load task details");
          }
        })
        .finally(() => {
          if (activeTaskRef.current.taskId === taskId) setLoading(false);
        });

      /* ── subtasks ── */
      if (Array.isArray(task.tasks) && task.tasks.length > 1) {
        const enrichedSubtasks = task.tasks.map((t) => ({
          ...t,
          title: t.title || "sample task",
          status: t.status || "pending",
        }));
        setSubtasks(enrichedSubtasks);
        setLoadingSubtasks(false);
      } else {
        const taskIdsToFetch = Array.isArray(task.tasks) && task.tasks.length === 1
          ? [task.tasks[0].id]
          : (Array.isArray(task.allTaskIds) ? task.allTaskIds : [taskId]);

        const validIds = taskIdsToFetch.filter((id) => id && !String(id).startsWith("temp-"));

        if (validIds.length === 0) {
          setSubtasks([]);
          setLoadingSubtasks(false);
        } else {
          Promise.all(
            validIds.map((id) =>
              axiosInstance
                .get(`api/task/${id}/subtasks`, { signal: controller.signal })
                .catch(() => ({ data: { data: { items: [] } } }))
            )
          ).then((responses) => {
            if (activeTaskRef.current.taskId !== taskId) return;
            const allItems = responses.flatMap((r) => r.data?.data?.items || []);
            setSubtasks(allItems);
          }).finally(() => {
            if (activeTaskRef.current.taskId === taskId) setLoadingSubtasks(false);
          });
        }
      }

      /* ── activity timeline ── */
      axiosInstance
        .get(`api/task/${taskId}/timeline`, { signal: controller.signal })
        .then(({ data }) => {
          if (activeTaskRef.current.taskId !== taskId) return;
          // Robust mapping for timeline data: items, data array, or direct body
          const items = data?.data?.data || data?.data?.items || (Array.isArray(data?.data) ? data.data : []);
          setTimeline(items);
        })
        .catch(() => {
          if (activeTaskRef.current.taskId === taskId) setTimeline([]);
        })
        .finally(() => {
          if (activeTaskRef.current.taskId === taskId) setLoadingTimeline(false);
        });

      // Special Note: We do NOT abort in this cleanup function to survive Strict Mode cycles.
      // The `activeTaskRef` logic above handles cleaning up when tasks actually change or close.
      return () => {
        // No-op for destroyed signal handling; activeTaskRef handles it
      };
    }, [open, task?.id, variant]); // taskId check handled within the effect

    /* ── derived / memoised values ──────────────────────────────────── */

    const rawTask = details || task;

    const members = useMemo(
      () => buildMemberList(details, task || rawTask),
      [details, task, rawTask]
    );

    /**
     * Assignees to display in the ASSIGNEE chip row.
     * Priority:
     *   1. task.assignees  – deputed-tasks list payload (array)
     *   2. details.assignees – fetched detail-API array
     *   3. details.assignee  – fetched detail-API single object
     *   4. members           – built from detail-API response
     */
    const displayAssignees = useMemo(() => {
      // 1. Prop-level assignees array (deputed-tasks table row)
      const rawProp = task?.assignees;
      if (Array.isArray(rawProp) && rawProp.length > 0) {
        return rawProp.map((a) => ({
          id: a.id,
          firstName: a.first_name || a.firstName || "",
          lastName: a.last_name || a.lastName || "",
          email: a.email || null,
          displayName:
            `${a.first_name || a.firstName || ""} ${a.last_name || a.lastName || ""}`.trim() ||
            "Unassigned",
        }));
      }

      // 2. Fetched details.assignees array
      const rawDetails = details?.assignees;
      if (Array.isArray(rawDetails) && rawDetails.length > 0) {
        return rawDetails.map((a) => ({
          id: a.id,
          firstName: a.first_name || a.firstName || "",
          lastName: a.last_name || a.lastName || "",
          email: a.email || null,
          displayName:
            `${a.first_name || a.firstName || ""} ${a.last_name || a.lastName || ""}`.trim() ||
            "Unassigned",
        }));
      }

      // 3. Fetched details.assignee single object
      const singleAssignee = details?.assignee;
      if (singleAssignee && typeof singleAssignee === "object") {
        const fn = singleAssignee.first_name || singleAssignee.firstName || "";
        const ln = singleAssignee.last_name || singleAssignee.lastName || "";
        const name = `${fn} ${ln}`.trim() || "Unassigned";
        return [
          {
            id: singleAssignee.id,
            firstName: fn,
            lastName: ln,
            email: singleAssignee.email || null,
            displayName: name,
          },
        ];
      }

      // 4. Fallback: members derived from detail-API
      return members.map((m) => {
        const parts = (m.name || "").trim().split(/\s+/).filter(Boolean);
        return {
          id: m.id,
          firstName: parts[0] || "",
          lastName: parts.slice(1).join(" ") || "",
          email: m.email,
          displayName: m.name || "Unassigned",
        };
      });
    }, [task?.assignees, details, members]);

    const { taskTitle, taskId, statusCfg, prioCfg, assigneeName, assigneeEmail, createdByName, projectName, hasDueDate } =
      useMemo(() => {
        const title = rawTask?.title || "Task Details";
        const ticketRaw = rawTask?.ticket_id || rawTask?.ticketId || rawTask?.ticketNumber || rawTask?.ticket_number || null;
        const id = ticketRaw ? String(ticketRaw) : rawTask?.id ? `Task-${rawTask.id}` : "";
        const sCfg = getStatusConfig(rawTask?.status);
        const pCfg = getPriorityConfig(rawTask?.priority);
        const firstMember = members[0];
        const aName = firstMember?.name || META_DASH;
        const aEmail = firstMember?.email || null;
        const cbName =
          rawTask?.given_by_name ||
          resolveFullName(
            rawTask?.givenBy?.first_name || rawTask?.givenBy?.FirstName,
            rawTask?.givenBy?.last_name || rawTask?.givenBy?.LastName
          ) ||
          resolveFullName(rawTask?.GivenByFirstName, rawTask?.GivenByLastName) ||
          "";
        const pName = rawTask?.project?.name || rawTask?.project_name || null;
        const due = Boolean(rawTask?.due_date);

        return {
          taskTitle: title,
          taskId: id,
          statusCfg: sCfg,
          prioCfg: pCfg,
          assigneeName: aName,
          assigneeEmail: aEmail,
          createdByName: cbName,
          projectName: pName,
          hasDueDate: due,
        };
      }, [rawTask, members]);

    const attachmentList = useMemo(
      () => buildAttachmentList(rawTask, details),
      [rawTask, details]
    );

    const canDeleteAttachments = useMemo(() => {
      if (!currentUser) return false;
      
      const role = String(currentUser.role || currentUser.Role || "").toLowerCase();
      if (role === "admin") return true;

      const curId = String(currentUser.id || currentUser.userId || currentUser.user_id);
      const isCreator = curId === String(rawTask?.givenBy?.id || rawTask?.givenBy?.userId || rawTask?.given_by_id || rawTask?.GivenById);
      
      if (isCreator) return true;

      // If user is an assignee, they can't delete
      const isAssignee = displayAssignees.some(a => String(a.id || a.userId || a.user_id) === curId);
      if (isAssignee) return false;

      return false; // Default: Only creator or admin can delete
    }, [currentUser, rawTask, displayAssignees]);

    /* ── stable callbacks ─────────────────────────────────────────────── */

    const handleNavigateFullPage = useCallback(() => {
      const isGrouped = Array.isArray(task?.tasks) && task.tasks.length > 1;
      const url = `/task/${rawTask?.id}${isGrouped ? "?is_grouped=1" : ""}`;
      window.open(url, "_blank");
    }, [rawTask?.id, task?.tasks]);

    const handleSubtaskNavigate = useCallback(
      (id) => {
        if (variant === "modal" || variant === "drawer") onClose();
        navigate(`/task/${id}`);
      },
      [navigate, onClose, variant]
    );

    const handleMemberToggle = useCallback(
      (memberId) =>
        setSelectedMemberId((prev) => (prev === memberId ? null : memberId)),
      []
    );

    /* ── early-exit guard ─────────────────────────────────────────────── */

    if ((!open && variant === "modal") || (!task && !loading && !rawTask)) return null;

    /* ── render ───────────────────────────────────────────────────────── */

    return (
      <div className="tdm-root">
        {/* ── Sticky header ────────────────────────────────────────────── */}
        <div className="tdm-header">
          <div className="tdm-header-left">
            <span
              className="tdm-status-chip"
              style={{
                background: statusCfg.bg,
                color: statusCfg.text,
                border: `1px solid ${statusCfg.border}`,
              }}
            >
              {rawTask?.status || "Pending"}
            </span>
            {taskId && <span className="tdm-task-id">{taskId}</span>}
          </div>

          <div className="tdm-header-right" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {variant === "drawer" && rawTask?.id && (
              <Tooltip title="View Full Page">
                <IconButton
                  size="small"
                  onClick={handleNavigateFullPage}
                  sx={{ color: "#6b7280", "&:hover": { backgroundColor: "#f3f4f6", color: "#111827" } }}
                >
                  <FaExpandAlt size={14} />
                </IconButton>
              </Tooltip>
            )}

            {(variant === "modal" || variant === "drawer") && (
              <button className="tdm-close-btn" onClick={onClose} aria-label="Close">
                <FaTimes size={14} />
              </button>
            )}
          </div>
        </div>

        {/* ── Body ────────────────────────────────────────────────────── */}
        <div className="tdm-body">
          <h2 className="tdm-title">{taskTitle}</h2>

          {/* ── 2-column metadata grid ─────────────────────────────── */}
          <div className="tdm-meta-grid">
            {/* Assignee */}
            <div className="tdm-meta-cell">
              <span className="tdm-meta-label">ASSIGNEE</span>
              <div className="tdm-meta-value tdm-meta-user">
                {displayAssignees.length === 0 ? (
                  <span className="tdm-meta-muted">{META_DASH}</span>
                ) : displayAssignees.length === 1 ? (
                  <UserAvatarTooltip
                    firstName={displayAssignees[0].firstName}
                    lastName={displayAssignees[0].lastName}
                    email={displayAssignees[0].email}
                    displayName={displayAssignees[0].displayName}
                  />
                ) : (
                  <AvatarGroup
                    max={4}
                    sx={{
                      '& .MuiAvatar-root': {
                        width: 28,
                        height: 28,
                        fontSize: '12px',
                        border: '2px solid #fff'
                      }
                    }}
                  >
                    {displayAssignees.map((a, i) => (
                      <UserAvatarTooltip
                        key={a.id || i}
                        firstName={a.firstName}
                        lastName={a.lastName}
                        email={a.email}
                        displayName={a.displayName}
                      />
                    ))}
                  </AvatarGroup>
                )}
              </div>
            </div>

            {/* Due Date */}
            <div className="tdm-meta-cell">
              <span className="tdm-meta-label">DUE DATE</span>
              <span className={`tdm-meta-value${!hasDueDate ? " tdm-meta-muted" : ""}`}>
                {hasDueDate ? formatDate(rawTask.due_date) : META_DASH}
              </span>
            </div>

            {/* Priority */}
            <div className="tdm-meta-cell">
              <span className="tdm-meta-label">PRIORITY</span>
              <span className="tdm-meta-value tdm-priority" style={{ color: prioCfg.color }}>
                <span className="tdm-priority-icon">{prioCfg.icon}</span>
                {rawTask?.priority || META_DASH}
              </span>
            </div>

            {/* Project */}
            <div className="tdm-meta-cell">
              <span className="tdm-meta-label">PROJECT</span>
              {projectName
                ? <span className="tdm-meta-value tdm-project-link">{projectName}</span>
                : <span className="tdm-meta-value tdm-meta-muted">{META_DASH}</span>
              }
            </div>
          </div>

          {/* ── Description ─────────────────────────────────────────── */}
          <div className="tdm-section">
            <SectionTitle title="DESCRIPTION" />
            <div className="tdm-description-box">
              <p className="tdm-description-text">
                {rawTask?.description || "No description available."}
              </p>
            </div>
          </div>

          {/* ── Attachments ─────────────────────────────────────────── */}
          {attachmentList.length > 0 && (
            <div className="tdm-section">
              <SectionTitle title={`ATTACHMENTS (${attachmentList.length})`} />
              <TaskAttachmentsPanel attachments={attachmentList} allowDelete={canDeleteAttachments} />
            </div>
          )}

          {/* ── Subordinate Tasks ───────────────────────────────────── */}
          {
            (subtasks.length > 0 && <div className="tdm-section">
              <SectionTitle title={`SUBORDINATE TASKS (${subtasks.length})`} />
              {loadingSubtasks ? (
                <div className="tdm-loading"><CircularProgress size={22} /></div>
              ) : subtasks.length === 0 ? (
                <p className="tdm-empty">No subordinate tasks.</p>
              ) : (
                <div className="tdm-subtask-list">
                  {subtasks.map((st) => (
                    <SubtaskRow key={st.id} st={st} onNavigate={handleSubtaskNavigate} />
                  ))}
                </div>
              )}
            </div>)
          }

          {/* ── Activity Timeline ───────────────────────────────────── */}
          <div className="tdm-section">
            <SectionTitle title="ACTIVITY TIMELINE" />
            {loadingTimeline ? (
              <div className="tdm-loading"><CircularProgress size={22} /></div>
            ) : timeline.length === 0 ? (
              <p className="tdm-empty">No activity recorded for this task.</p>
            ) : (
              <div
                className="tdm-timeline-list"
                style={{ marginLeft: "12px", borderLeft: "2px solid #e5e7eb", paddingLeft: "24px" }}
              >
                {timeline.map((event, idx) => (
                  <ActivityTimelineEvent key={event.id || idx} event={event} idx={idx} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

TaskDetailsModal.displayName = "TaskDetailsModal";

export default TaskDetailsModal;
